import React, { useMemo, useCallback } from "react";
import { Box, View } from "components/ui";
import { useWindowDimensions, ActivityIndicator, ScrollView } from "react-native";
import ArticleCard from "components/general/Card/ArticleCard";
import { useBreakpointValue } from "@gluestack-style/react";
import { useIsMobile } from "utils/hooks/useIsMobile";

type ArticleCardListProps = {
	list?: any;
	header?: any;
	scrollEnabled?: boolean;
	onEndReached?: () => void;
	hasMore?: boolean;
};

const ArticleCardList = React.memo(function ArticleCardList({ list, header, scrollEnabled, onEndReached, hasMore }: ArticleCardListProps) {
	const { width: windowWidth } = useWindowDimensions();
	const isMobile = useIsMobile();
	const noColumn = useBreakpointValue({
		base: 2,
		lg: 3,
	});
	const itemWidth = useBreakpointValue({
		base: "$1/2",
		lg: "$1/3",
	});

	// Memoize expensive calculations
	const groupedItems = useMemo(() => {
		if (!list) return [];

		const grouped = [];
		for (let i = 0; i < list.length; i += noColumn) {
			grouped.push(list.slice(i, i + noColumn));
		}
		return grouped;
	}, [list, noColumn]);

	const renderFooter = useCallback(() => {
		if (!hasMore) return null;
		return (
			<Box paddingVertical={20} alignItems="center">
				<ActivityIndicator size="large" color="#1A428A" />
			</Box>
		);
	}, [hasMore]);

	const handleScroll = useCallback(
		(event: any) => {
			const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
			const isCloseToBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - 20;

			if (isCloseToBottom && onEndReached) {
				onEndReached();
			}
		},
		[onEndReached],
	);

	// Memoize the Wrapper component
	const Wrapper = useMemo(() => {
		return function WrapperComponent(props: any) {
			if (isMobile) {
				return props.children;
			}

			return (
				<View>
					<Box>{props.children}</Box>
				</View>
			);
		};
	}, [isMobile]);

	if (!list) {
		return null;
	}
	const itemHeight = 400;
	const gap = 8;
	const numberOfRows = Math.ceil(list.length / noColumn);
	const totalGapSpace = (numberOfRows - 1) * gap;
	const dynamicHeight = numberOfRows * itemHeight + totalGapSpace;

	return (
		<Wrapper>
			{groupedItems.map((row, rowIndex) => (
				<Box key={rowIndex} flexDirection="row" width="100%">
					{row.map((item: any, itemIndex: number) => (
						<Box key={item.id || `${rowIndex}-${itemIndex}`} width={itemWidth} padding={"$2"}>
							<ArticleCard data={item} />
						</Box>
					))}
				</Box>
			))}
		</Wrapper>
	);
});

export default ArticleCardList;
